import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { signIn, signUp, supabase } from '../lib/supabase'
import { Eye, EyeOff, Shield, UserCheck, Calendar, BookOpen, CloudSun } from 'lucide-react'

const ROLES = [
  { value: 'admin', label: 'Admin / Owner', icon: Shield, desc: 'Manage the full operation' },
  { value: 'instructor', label: 'Instructor', icon: UserCheck, desc: 'View schedule & shifts' },
]

const INPUT_BASE = {
  width: '100%',
  padding: '0.75rem 1rem',
  background: 'rgba(255,255,255,0.06)',
  border: '1.5px solid rgba(255,255,255,0.12)',
  borderRadius: 10,
  color: 'white',
  fontFamily: 'DM Sans, sans-serif',
  fontSize: '0.9375rem',
  outline: 'none',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  boxSizing: 'border-box',
}

function FocusInput({ style = {}, ...props }) {
  const [focused, setFocused] = useState(false)
  return (
    <input
      {...props}
      style={{
        ...INPUT_BASE,
        ...style,
        borderColor: focused ? 'var(--teal)' : 'rgba(255,255,255,0.12)',
        boxShadow: focused ? '0 0 0 3px rgba(14,170,164,0.18)' : 'none',
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  )
}

function WavePanel() {
  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(160deg, #0B1E3D 0%, #0d2847 50%, #0a1f3a 100%)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '3rem',
    }}>
      {/* Ambient orbs */}
      <div style={{
        position: 'absolute', top: '15%', left: '20%',
        width: 320, height: 320,
        background: 'radial-gradient(circle, rgba(14,170,164,0.18) 0%, transparent 70%)',
        borderRadius: '50%', filter: 'blur(40px)',
      }} />
      <div style={{
        position: 'absolute', bottom: '20%', right: '10%',
        width: 260, height: 260,
        background: 'radial-gradient(circle, rgba(232,97,74,0.14) 0%, transparent 70%)',
        borderRadius: '50%', filter: 'blur(40px)',
      }} />

      {/* Animated wave lines */}
      <svg
        style={{ position: 'absolute', bottom: 0, left: 0, right: 0, opacity: 0.12 }}
        viewBox="0 0 800 200" preserveAspectRatio="none"
      >
        <path d="M0,100 C133,40 267,160 400,100 C533,40 667,160 800,100 L800,200 L0,200Z" fill="var(--teal)" />
      </svg>
      <svg
        style={{ position: 'absolute', bottom: 0, left: 0, right: 0, opacity: 0.07 }}
        viewBox="0 0 800 200" preserveAspectRatio="none"
      >
        <path d="M0,120 C100,60 200,180 400,120 C600,60 700,180 800,120 L800,200 L0,200Z" fill="var(--coral)" />
      </svg>

      {/* Content */}
      <div style={{ position: 'relative', textAlign: 'center', maxWidth: 340 }}>
        {/* Logo mark */}
        <div style={{
          width: 72, height: 72,
          background: 'linear-gradient(135deg, var(--coral) 0%, #c94d38 100%)',
          borderRadius: '50% 50% 50% 0',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          transform: 'rotate(-45deg)',
          marginBottom: '1.75rem',
          boxShadow: '0 8px 32px rgba(232,97,74,0.35)',
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" style={{ transform: 'rotate(45deg)' }}>
            <path d="M2 12 C5 8 8 16 12 12 C16 8 19 16 22 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
            <path d="M2 16 C5 12 8 20 12 16 C16 12 19 20 22 16" stroke="white" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.6"/>
          </svg>
        </div>

        <h1 style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: '2.5rem', fontWeight: 900,
          color: 'white', lineHeight: 1.1,
          marginBottom: '0.75rem',
        }}>
          WaveSync
        </h1>
        <p style={{
          color: 'rgba(255,255,255,0.45)',
          fontSize: '1rem', lineHeight: 1.6,
          marginBottom: '3rem',
        }}>
          The all-in-one platform for<br />surf school operations.
        </p>

        {/* Feature pills */}
        {[
          { Icon: Calendar, text: 'Smart scheduling' },
          { Icon: BookOpen, text: 'Lesson management' },
          { Icon: CloudSun, text: 'Real-time weather' },
        ].map(({ Icon, text }) => (
          <div key={text} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 100, padding: '0.5rem 1rem',
            marginBottom: 10, width: 'fit-content', margin: '0 auto 10px',
          }}>
            <Icon size={14} color="var(--teal)" strokeWidth={1.75} />
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8125rem', fontFamily: 'DM Sans, sans-serif' }}>
              {text}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function LoginPage() {
  const navigate = useNavigate()
  const [mode, setMode] = useState('login')
  const [role, setRole] = useState('admin')
  const [form, setForm] = useState({ email: '', password: '', full_name: '' })
  const [showPw, setShowPw] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      if (mode === 'login') {
        const { error } = await signIn(form.email, form.password)
        if (error) throw error
        navigate('/')
      } else {
        const { data, error } = await signUp(form.email, form.password, {
          full_name: form.full_name,
          role,
        })
        if (error) throw error
        if (role === 'admin' && data?.user) {
          await supabase.from('profiles')
            .update({ platform_approved: false })
            .eq('id', data.user.id)
        }
        setError('Check your email to confirm your account, then log in.')
        setMode('login')
      }
    } catch (err) {
      setError(err.message || 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }))
  const isSuccess = error.startsWith('Check')

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      background: '#0B1E3D',
    }}>
      {/* Left decorative panel — hidden on small screens via inline media query workaround */}
      <div style={{
        flex: '0 0 45%',
        display: 'flex',
        minHeight: '100vh',
      }} className="login-left-panel">
        <WavePanel />
      </div>

      {/* Right form panel */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2.5rem 2rem',
        background: 'rgba(255,255,255,0.015)',
        borderLeft: '1px solid rgba(255,255,255,0.06)',
        minHeight: '100vh',
        overflowY: 'auto',
      }}>
        <div style={{ width: '100%', maxWidth: 420 }}>

          {/* Mobile logo (only shows when panel is hidden) */}
          <div className="login-mobile-logo" style={{ display: 'none', textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{
              width: 52, height: 52,
              background: 'linear-gradient(135deg, var(--coral) 0%, #c94d38 100%)',
              borderRadius: '50% 50% 50% 0', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center',
              transform: 'rotate(-45deg)', marginBottom: '0.875rem',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ transform: 'rotate(45deg)' }}>
                <path d="M2 12 C5 8 8 16 12 12 C16 8 19 16 22 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
              </svg>
            </div>
            <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.75rem', fontWeight: 900, color: 'white' }}>WaveSync</h1>
          </div>

          {/* Header */}
          <div style={{ marginBottom: '2rem' }}>
            <h2 style={{
              fontFamily: 'Playfair Display, serif',
              fontSize: '1.875rem', fontWeight: 700,
              color: 'white', marginBottom: '0.375rem',
            }}>
              {mode === 'login' ? 'Welcome back' : 'Create account'}
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.875rem', fontFamily: 'DM Sans, sans-serif' }}>
              {mode === 'login'
                ? 'Sign in to your WaveSync account'
                : 'Get started with WaveSync today'}
            </p>
          </div>

          {/* Mode toggle */}
          <div style={{
            display: 'flex',
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 12, padding: 4, marginBottom: '1.75rem',
          }}>
            {['login', 'signup'].map(m => (
              <button key={m} onClick={() => { setMode(m); setError('') }} style={{
                flex: 1, padding: '0.5625rem 1rem',
                border: 'none', borderRadius: 9,
                fontFamily: 'DM Sans, sans-serif',
                fontSize: '0.875rem', fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.2s',
                background: mode === m
                  ? 'linear-gradient(135deg, rgba(232,97,74,0.9) 0%, rgba(200,77,56,0.9) 100%)'
                  : 'transparent',
                color: mode === m ? 'white' : 'rgba(255,255,255,0.4)',
                boxShadow: mode === m ? '0 2px 12px rgba(232,97,74,0.3)' : 'none',
              }}>
                {m === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.125rem' }}>

            {/* Role selector — signup only */}
            {mode === 'signup' && (
              <div>
                <label style={{
                  display: 'block', marginBottom: 8,
                  color: 'rgba(255,255,255,0.4)',
                  fontSize: '0.8125rem', fontFamily: 'DM Sans, sans-serif',
                  fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase',
                }}>
                  I am a
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8 }}>
                  {ROLES.map(r => {
                    const Icon = r.icon
                    const active = role === r.value
                    return (
                      <button key={r.value} type="button" onClick={() => setRole(r.value)} style={{
                        padding: '1rem 0.75rem',
                        border: '1.5px solid',
                        borderColor: active ? 'var(--teal)' : 'rgba(255,255,255,0.1)',
                        borderRadius: 12,
                        background: active
                          ? 'linear-gradient(135deg, rgba(14,170,164,0.15) 0%, rgba(14,170,164,0.08) 100%)'
                          : 'rgba(255,255,255,0.03)',
                        color: active ? 'var(--teal)' : 'rgba(255,255,255,0.4)',
                        cursor: 'pointer', textAlign: 'center',
                        transition: 'all 0.2s',
                        fontFamily: 'DM Sans, sans-serif',
                        boxShadow: active ? '0 0 0 1px rgba(14,170,164,0.2), 0 4px 16px rgba(14,170,164,0.1)' : 'none',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}>
                          <Icon size={20} strokeWidth={1.75} />
                        </div>
                        <div style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{r.label}</div>
                        <div style={{ fontSize: '0.6875rem', opacity: 0.6, marginTop: 2 }}>{r.desc}</div>
                      </button>
                    )
                  })}
                </div>
                <div style={{
                  marginTop: 10, padding: '0.625rem 0.875rem',
                  background: 'rgba(14,170,164,0.06)',
                  border: '1px solid rgba(14,170,164,0.18)',
                  borderRadius: 8, fontSize: '0.75rem',
                  color: 'rgba(255,255,255,0.4)', lineHeight: 1.55,
                  fontFamily: 'DM Sans, sans-serif',
                }}>
                  <strong style={{ color: 'rgba(255,255,255,0.6)' }}>Booking a lesson?</strong>{' '}
                  Use the signup link provided by your surf school.
                </div>
              </div>
            )}

            {/* Full name — signup only */}
            {mode === 'signup' && (
              <div>
                <label style={{
                  display: 'block', marginBottom: 6,
                  color: 'rgba(255,255,255,0.4)', fontSize: '0.8125rem',
                  fontFamily: 'DM Sans, sans-serif', fontWeight: 500,
                  letterSpacing: '0.04em', textTransform: 'uppercase',
                }}>Full Name</label>
                <FocusInput
                  placeholder="Your full name"
                  value={form.full_name}
                  onChange={set('full_name')}
                  required
                  autoComplete="name"
                />
              </div>
            )}

            {/* Email */}
            <div>
              <label style={{
                display: 'block', marginBottom: 6,
                color: 'rgba(255,255,255,0.4)', fontSize: '0.8125rem',
                fontFamily: 'DM Sans, sans-serif', fontWeight: 500,
                letterSpacing: '0.04em', textTransform: 'uppercase',
              }}>Email</label>
              <FocusInput
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={set('email')}
                required
                autoComplete="email"
              />
            </div>

            {/* Password */}
            <div>
              <label style={{
                display: 'block', marginBottom: 6,
                color: 'rgba(255,255,255,0.4)', fontSize: '0.8125rem',
                fontFamily: 'DM Sans, sans-serif', fontWeight: 500,
                letterSpacing: '0.04em', textTransform: 'uppercase',
              }}>Password</label>
              <div style={{ position: 'relative' }}>
                <FocusInput
                  type={showPw ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={form.password}
                  onChange={set('password')}
                  minLength={6}
                  required
                  style={{ paddingRight: '2.75rem' }}
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                />
                <button type="button" onClick={() => setShowPw(!showPw)} style={{
                  position: 'absolute', right: 12, top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none', border: 'none',
                  cursor: 'pointer', color: 'rgba(255,255,255,0.3)',
                  padding: 0, display: 'flex', alignItems: 'center',
                  transition: 'color 0.2s',
                }}>
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Error / success message */}
            {error && (
              <div style={{
                padding: '0.75rem 1rem', borderRadius: 10,
                background: isSuccess ? 'rgba(14,170,164,0.1)' : 'rgba(232,97,74,0.1)',
                border: `1px solid ${isSuccess ? 'rgba(14,170,164,0.25)' : 'rgba(232,97,74,0.25)'}`,
                fontSize: '0.8125rem', lineHeight: 1.5,
                color: isSuccess ? '#4dd5d0' : '#f0876e',
                fontFamily: 'DM Sans, sans-serif',
              }}>
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '0.875rem',
                background: loading
                  ? 'rgba(232,97,74,0.5)'
                  : 'linear-gradient(135deg, var(--coral) 0%, #c94d38 100%)',
                border: 'none', borderRadius: 12,
                color: 'white', fontFamily: 'DM Sans, sans-serif',
                fontSize: '0.9375rem', fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                boxShadow: loading ? 'none' : '0 4px 20px rgba(232,97,74,0.35)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                marginTop: 2,
              }}
            >
              {loading ? (
                <>
                  <div style={{
                    width: 16, height: 16,
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: 'white',
                    borderRadius: '50%',
                    animation: 'spin 0.7s linear infinite',
                  }} />
                  {mode === 'login' ? 'Signing in…' : 'Creating account…'}
                </>
              ) : (
                mode === 'login' ? 'Sign In →' : 'Create Account →'
              )}
            </button>
          </form>

          <p style={{
            textAlign: 'center', marginTop: '1.5rem',
            fontSize: '0.8125rem', color: 'rgba(255,255,255,0.25)',
            fontFamily: 'DM Sans, sans-serif',
          }}>
            {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <button
              type="button"
              onClick={() => { setMode(mode === 'login' ? 'signup' : 'login'); setError('') }}
              style={{
                background: 'none', border: 'none',
                color: 'var(--teal)', cursor: 'pointer',
                fontSize: '0.8125rem', fontFamily: 'DM Sans, sans-serif',
                fontWeight: 600,
              }}
            >
              {mode === 'login' ? 'Create account' : 'Sign in'}
            </button>
          </p>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 768px) {
          .login-left-panel { display: none !important; }
          .login-mobile-logo { display: block !important; }
        }
      `}</style>
    </div>
  )
}
